import { useState, useRef, useEffect, useCallback, forwardRef } from 'react'
import { clsx } from 'clsx'
import styles from './SearchableSelect.module.css'

interface SearchableSelectProps {
  label: string
  name: string
  options: string[]
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  hint?: string
  icon?: string
  className?: string
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}

export const SearchableSelect = forwardRef<HTMLInputElement, SearchableSelectProps>(({
  label,
  name,
  options,
  placeholder = 'Inizia a digitare per cercare...',
  required = false,
  disabled = false,
  error,
  hint,
  icon,
  className,
  value,
  onChange,
  onBlur,
  ...rest
}, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [isMobile, setIsMobile] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const internalInputRef = useRef<HTMLInputElement | null>(null)
  const mobileSearchRef = useRef<HTMLInputElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  const inputId = `searchable-${name}`

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const filtered = options.filter(opt =>
    opt.toLowerCase().includes((isMobile && isOpen ? searchTerm : (value || '')).toLowerCase())
  )

  const open = useCallback(() => {
    if (disabled) return
    setIsOpen(true)
    setHighlightedIndex(-1)
    if (isMobile) {
      setSearchTerm(value || '')
      document.body.style.overflow = 'hidden'
    }
  }, [disabled, isMobile, value])

  const close = useCallback(() => {
    setIsOpen(false)
    setSearchTerm('')
    setHighlightedIndex(-1)
    if (isMobile) {
      document.body.style.overflow = ''
    }
  }, [isMobile])

  const selectOption = useCallback((option: string) => {
    if (onChange) {
      const syntheticEvent = {
        target: { value: option, name },
      } as React.ChangeEvent<HTMLInputElement>
      onChange(syntheticEvent)
    }
    close()
    internalInputRef.current?.focus()
  }, [onChange, close, name])

  const clearValue = useCallback(() => {
    if (onChange) {
      const syntheticEvent = {
        target: { value: '', name },
      } as React.ChangeEvent<HTMLInputElement>
      onChange(syntheticEvent)
    }
    setSearchTerm('')
  }, [onChange, name])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, close])

  useEffect(() => {
    if (!isOpen) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, close])

  useEffect(() => {
    if (isOpen && isMobile && mobileSearchRef.current) {
      setTimeout(() => mobileSearchRef.current?.focus(), 100)
    }
  }, [isOpen, isMobile])

  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightedIndex])

  const handleDesktopInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e)
    if (!isOpen) setIsOpen(true)
    setHighlightedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      e.preventDefault()
      open()
      return
    }
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => (prev < filtered.length - 1 ? prev + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filtered.length - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filtered[highlightedIndex]) {
          selectOption(filtered[highlightedIndex])
        }
        break
      case 'Tab':
        close()
        break
    }
  }

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text
    const idx = text.toLowerCase().indexOf(query.toLowerCase())
    if (idx === -1) return text
    return (
      <>
        {text.slice(0, idx)}
        <mark className={styles.highlight}>{text.slice(idx, idx + query.length)}</mark>
        {text.slice(idx + query.length)}
      </>
    )
  }

  const currentSearch = isMobile && isOpen ? searchTerm : (value || '')

  const setRefs = useCallback((el: HTMLInputElement | null) => {
    internalInputRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el
  }, [ref])

  const wrapperClasses = clsx(
    styles.wrapper,
    { [styles.hasError]: error, [styles.hasIcon]: icon, [styles.disabled]: disabled },
    className
  )

  return (
    <div className={wrapperClasses} ref={containerRef}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.inputWrapper}>
        {icon && (
          <span className={styles.iconWrapper}>
            <i className={`bi bi-${icon}`} />
          </span>
        )}

        <input
          ref={setRefs}
          id={inputId}
          name={name}
          type="text"
          readOnly={isMobile}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          role="combobox"
          className={clsx(styles.input, { [styles.hasValue]: !!value })}
          value={value || ''}
          onChange={handleDesktopInputChange}
          onFocus={() => { if (!isMobile && !isOpen) open() }}
          onClick={() => { if (isMobile) open() }}
          onKeyDown={handleKeyDown}
          onBlur={(e) => {
            if (!containerRef.current?.contains(e.relatedTarget as Node)) {
              onBlur?.(e)
              if (!isMobile) close()
            }
          }}
          {...rest}
        />

        {value && !disabled && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={(e) => { e.stopPropagation(); clearValue() }}
            tabIndex={-1}
            aria-label="Cancella selezione"
          >
            <i className="bi bi-x-lg" />
          </button>
        )}

        <span className={styles.chevron} onClick={() => { if (!disabled) isOpen ? close() : open() }}>
          <i className={clsx('bi', isOpen ? 'bi-chevron-up' : 'bi-chevron-down')} />
        </span>
      </div>

      {/* Desktop Dropdown */}
      {isOpen && !isMobile && (
        <div className={styles.dropdown} ref={dropdownRef} role="listbox" aria-label={label}>
          {filtered.length === 0 ? (
            <div className={styles.noResults}>
              <i className="bi bi-search" />
              <span>Nessun fornitore trovato</span>
            </div>
          ) : (
            <div className={styles.optionsList}>
              {filtered.map((option, idx) => (
                <button
                  key={option}
                  type="button"
                  role="option"
                  ref={el => { itemRefs.current[idx] = el }}
                  aria-selected={option === value}
                  className={clsx(styles.option, {
                    [styles.optionHighlighted]: idx === highlightedIndex,
                    [styles.optionSelected]: option === value,
                    [styles.optionAltro]: option === 'Altro fornitore',
                  })}
                  onClick={() => selectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                >
                  <span className={styles.optionText}>
                    {highlightMatch(option, currentSearch)}
                  </span>
                  {option === value && (
                    <i className={clsx('bi bi-check-lg', styles.checkIcon)} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mobile Bottom Sheet */}
      {isOpen && isMobile && (
        <div className={styles.overlay} onClick={close}>
          <div className={styles.bottomSheet} onClick={e => e.stopPropagation()}>
            <div className={styles.sheetHandle} />

            <div className={styles.sheetHeader}>
              <span className={styles.sheetTitle}>{label}</span>
              <button type="button" className={styles.sheetClose} onClick={close}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className={styles.sheetSearch}>
              <i className="bi bi-search" />
              <input
                ref={mobileSearchRef}
                type="text"
                className={styles.sheetSearchInput}
                placeholder="Cerca fornitore..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setHighlightedIndex(-1) }}
              />
              {searchTerm && (
                <button
                  type="button"
                  className={styles.sheetSearchClear}
                  onClick={() => setSearchTerm('')}
                >
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>

            <div className={styles.sheetList} role="listbox" aria-label={label}>
              {filtered.length === 0 ? (
                <div className={styles.noResults}>
                  <i className="bi bi-search" />
                  <span>Nessun fornitore trovato</span>
                </div>
              ) : (
                filtered.map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="option"
                    aria-selected={option === value}
                    className={clsx(styles.sheetOption, {
                      [styles.sheetOptionSelected]: option === value,
                      [styles.optionAltro]: option === 'Altro fornitore',
                    })}
                    onClick={() => selectOption(option)}
                  >
                    <span className={styles.optionText}>
                      {highlightMatch(option, searchTerm)}
                    </span>
                    {option === value && (
                      <span className={styles.sheetCheck}>
                        <i className="bi bi-check-circle-fill" />
                      </span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <p id={`${inputId}-error`} className={styles.error} role="alert">
          <i className="bi bi-exclamation-circle" />
          {error}
        </p>
      )}

      {hint && !error && (
        <p id={`${inputId}-hint`} className={styles.hint}>
          <i className="bi bi-info-circle" />
          {hint}
        </p>
      )}
    </div>
  )
})

SearchableSelect.displayName = 'SearchableSelect'
